<template>
  <div class="pivot-trajectory">
    <div v-if="loading" class="col-xs-12 center-xs mt-2">
      <spinner /> Carregando trajeto do pivô...
    </div>
    <div v-else-if="!days.length" class="pivot-sensor-chart-empty">
      Não há leituras de posição (GPS) do pivô no período.
    </div>
    <template v-else>
      <div class="pivot-trajectory-title">Trajeto do equipamento na borda do pivô central</div>
      <div class="pivot-trajectory-subtitle">Fonte: {{ posSensorName }} · latitude/longitude combinadas por horário</div>

      <div class="pivot-trajectory-panel mt-2">
        <div class="pivot-trajectory-panel-header">
          <span class="pivot-trajectory-panel-title">Trajetória e ângulo percorrido (visão polar)</span>
          <select v-model="day" class="pivot-trajectory-day-select">
            <option v-for="d in days" :key="d" :value="d">{{ formatDay(d) }} ({{ pointsByDay.get(d).length }} pts)</option>
          </select>
        </div>
        <pivot-trajectory-map :points="pointsByDay.get(day) || []" :radius="radius" :stats="dayStatsForSelected" :max-width="mapMaxWidth" />
      </div>

      <div class="pivot-trajectory-panel mt-2">
        <div class="pivot-trajectory-panel-title mb-1">Ângulo (°) ao longo do tempo — todos os dias sobrepostos</div>
        <apexchart width="100%" height="240" :series="angleSeries" :options="angleOptions"></apexchart>
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import { monitChart } from "@/api";
import { buildTrajectoryPoints, attachPressure, maxRadius, groupByDay, dayStats } from "../pirGeo";
import PivotTrajectoryMap from "./PivotTrajectoryMap.vue";

const LOOKBACK_DAYS = 14;
const DAY_COLORS = ["#3d9161", "#eb753e", "#298abe", "#b92625", "#8a5cc9", "#c9a227", "#3aada8", "#c9508a"];

export default {
  name: "pivot-trajectory-chart",
  components: {
    PivotTrajectoryMap,
  },
  props: {
    // Sensor de posição (SPG, modelo GPS) — latitude/longitude do equipamento.
    posSensorName: String,
    // Sensor PIR do DIR (CAD_DIR.EQUIP_ON_PIR) — mesma fonte da "Pressão:" do
    // disco do atuador. Amostrado independente da posição, então os pontos
    // recebem a leitura de pressão mais próxima no tempo (ver attachPressure).
    pressureSensorName: String,
    centerLat: Number,
    centerLon: Number,
    isMobile: Boolean,
  },
  data() {
    return {
      loading: true,
      controller: null,
      points: [],
      day: null,
    };
  },
  computed: {
    days() {
      return [...this.pointsByDay.keys()];
    },
    pointsByDay() {
      return groupByDay(this.points);
    },
    radius() {
      return maxRadius(this.points);
    },
    dayStatsForSelected() {
      return dayStats(this.pointsByDay.get(this.day) || []);
    },
    // O mapa agora é um quadrado responsivo (aspect-ratio 1:1 no CSS) — o teto
    // só evita que fique enorme em painéis muito largos no desktop.
    mapMaxWidth() {
      return this.isMobile ? 480 : 640;
    },
    angleSeries() {
      return this.days
        .slice(0, DAY_COLORS.length)
        .map((day) => ({
          name: this.formatDay(day),
          data: this.pointsByDay.get(day).map((p) => [this.timeOfDayMs(p.time), Math.round(p.angle * 10) / 10]),
        }))
        .reverse();
    },
    angleOptions() {
      return {
        chart: {
          id: "pivot-trajectory-angle-time",
          type: "line",
          zoom: { enabled: true, autoScaleYaxis: true },
          animations: { enabled: false },
          toolbar: { offsetY: -2 },
        },
        xaxis: {
          type: "datetime",
          labels: { datetimeUTC: false, format: "HH:mm" },
          title: { text: "Horário do dia", style: { fontSize: "10px" } },
        },
        yaxis: {
          min: 0,
          max: 360,
          tickAmount: 4,
          labels: { formatter: (v) => `${Math.round(v)}°` },
        },
        stroke: { curve: "straight", width: 2 },
        dataLabels: { enabled: false },
        markers: { size: 2 },
        tooltip: {
          x: { format: "HH:mm" },
          y: { formatter: (v) => `${v}°` },
        },
        legend: { show: true, position: "bottom", fontSize: "11px" },
        colors: DAY_COLORS.slice(0, this.days.length).reverse(),
      };
    },
  },
  created() {
    this.controller = new AbortController();
    this.fetchTrajectory();
  },
  beforeDestroy() {
    this.controller.abort();
  },
  methods: {
    formatDay(day) {
      return moment(day).format("DD/MM/YYYY");
    },
    // Normaliza todos os pontos pro mesmo dia de referência (hoje), preservando só o
    // horário — é o que permite sobrepor vários dias no mesmo eixo X no gráfico de baixo.
    timeOfDayMs(t) {
      const m = moment(t);
      return moment().startOf("day").add(m.hours(), "hours").add(m.minutes(), "minutes").add(m.seconds(), "seconds").valueOf();
    },
    // O backend devolve TYPE "PRESSÃO" (tudo maiúsculo) pro sensor PIR, mas
    // "Posição"/"Vazão" com capitalização normal — inconsistência do cadastro
    // no banco, não do CONDITION.TYPES enviado na requisição. Comparação
    // case-insensitive evita depender de acertar o case exato de cada tipo.
    toSeries(logs, type) {
      const target = type.toUpperCase();
      return logs
        .filter((l) => (l.TYPE || "").toUpperCase() === target)
        .map((l) => [new Date(l.TIME.replace(/ /g, "T")).getTime(), l.VALUE]);
    },
    fetchPressure(start, end) {
      if (!this.pressureSensorName) return Promise.resolve([]);
      const data = {
        EQP: "SENSOR",
        NAME: this.pressureSensorName,
        COLUMN: "UNIT",
        CONDITION: { TYPES: ["Pressão"], UNITS: ["bar"] },
        START: start.format("YYYY-MM-DD HH:mm"),
        END: end.format("YYYY-MM-DD HH:mm"),
      };
      return monitChart
        .create(data, this.controller)
        .then((ret) => {
          if (!ret.status) throw new Error(ret.log || "Falha ao buscar pressão do pivô.");
          const logs = Array.isArray(ret.value[1]) ? ret.value[0] : ret.value;
          return this.toSeries(logs, "Pressão");
        })
        .catch((err) => {
          console.error(err);
          return [];
        });
    },
    fetchTrajectory() {
      if (!this.posSensorName || this.centerLat == null || this.centerLon == null) {
        this.loading = false;
        return;
      }
      this.loading = true;
      const end = moment();
      const start = moment().subtract(LOOKBACK_DAYS, "days").hours(0).minutes(0);
      const data = {
        EQP: "SENSOR",
        NAME: this.posSensorName,
        COLUMN: "UNIT",
        CONDITION: {
          TYPES: ["Latitude", "Longitude"],
          UNITS: ["º", "º"],
        },
        START: start.format("YYYY-MM-DD HH:mm"),
        END: end.format("YYYY-MM-DD HH:mm"),
      };
      Promise.all([
        monitChart
          .create(data, this.controller)
          .then((ret) => {
            if (!ret.status) throw new Error(ret.log || "Falha ao buscar trajeto do pivô.");
            const logs = Array.isArray(ret.value[1]) ? ret.value[0] : ret.value;
            const latSeries = this.toSeries(logs, "Latitude");
            const lonSeries = this.toSeries(logs, "Longitude");
            return buildTrajectoryPoints(latSeries, lonSeries, this.centerLat, this.centerLon);
          })
          .catch((err) => {
            console.error(err);
            return [];
          }),
        this.fetchPressure(start, end),
      ])
        .then(([points, pressureSeries]) => {
          this.points = attachPressure(points, pressureSeries);
          const days = [...groupByDay(this.points).keys()];
          this.day = days[0] || null;
        })
        .then(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.pivot-trajectory-title {
  font-weight: 600;
  font-size: 1.05em;
}
.pivot-trajectory-subtitle {
  font-size: 0.78em;
  color: #999;
  margin-bottom: 4px;
}
.pivot-trajectory-panel {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 10px 12px;
  height: 100%;
}
.pivot-trajectory-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}
.pivot-trajectory-panel-title {
  font-size: 0.75em;
  font-weight: 700;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.pivot-trajectory-day-select {
  font-size: 0.78em;
  padding: 2px 4px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
}
</style>
